'use client';

/**
 * HEADER — navigation groupée (modèle index3CORANNDIAMSYL.html) :
 *  Accueil · READ (CTA doré — page fusionnée Livre/Lecture/Écoute/Sourates) · Académie ▾ · Sciences ▾ · Vidéos ▾ · Espace ▾ · Univers (CTA)
 * Desktop : menus déroulants · Mobile : panneau plein écran.
 */
import { useEffect, useState } from 'react';
import { useApp, type Section, type AcademyLevel } from '@/lib/store';
import { UI_LANGS, DICTS } from '@/lib/i18n';
import {
  BookOpen, BookMarked, Headphones, GraduationCap, PenLine, Wrench, Home, Languages, ScrollText,
  Landmark, Compass, CalendarDays, Sparkles, Video, Images, Volume2, VolumeX,
  ChevronDown, Menu, X, Ear, Brain, Crown, Library, Atom, Radio, Users, Gem,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { onSpeechState, stopSpeech } from '@/lib/speech';

type MenuKey = 'academy' | 'savoir' | 'media' | 'tools';

interface NavItem {
  key: Section;
  label: string;
  desc?: string;
  icon: React.ReactNode;
  badge?: string;
}

export default function Header() {
  const { uiLang, setUiLang, section, setSection, setAcademyLevel } = useApp();
  const t = DICTS[uiLang];
  const [voiceMode, setVoiceMode] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [openMenu, setOpenMenu] = useState<MenuKey | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const off = onSpeechState((s) => setSpeaking(s.speaking));
    return off;
  }, []);

  const toggleVoice = () => {
    if (voiceMode) stopSpeech();
    setVoiceMode(!voiceMode);
    try { localStorage.setItem('q1000_voice', voiceMode ? '0' : '1'); } catch { /* ignore */ }
  };

  const jump = (key: Section) => {
    setOpenMenu(null);
    setMobileOpen(false);
    setSection(key);
    // Mode vocal global : la section ciblée se présente à l'ouverture
    if (!voiceMode) return;
    const intros: Record<string, { fr: string; en: string; ar: string }> = {
      livre: { fr: 'Le Livre Saint sous toutes ses graphies, et ses 29 éditions prestige', en: 'The Holy Book in all its spellings, and its 29 prestige editions', ar: 'الكتاب المقدس بجميع رسوماته وإصداراته الـ٢٩ الفاخرة' },
      sciences: { fr: 'Les Sciences Coraniques : dix disciplines des Ulūm al-Qurʾān', en: 'Quranic Sciences: ten disciplines of ʿUlūm al-Qurʾān', ar: 'علوم القرآن: عشرة فنون من علوم القرآن' },
      miracles: { fr: 'Les Miracles du Coran, confirmés par la science moderne', en: 'The Miracles of the Quran, confirmed by modern science', ar: 'معجزات القرآن التي أكدتها العلوم الحديثة' },
      conference: { fr: 'La Salle Royale : la visioconférence pour apprendre ensemble', en: 'The Royal Hall: video conference to learn together', ar: 'القاعة الملكية: الاجتماع المرئي للتعلم معًا' },
      espace: { fr: "L'Espace Royal : identification, rang, Khatma, Hifz et Quiz", en: 'The Royal Space: identification, rank, Khatma, Hifz and Quiz', ar: 'الفضاء الملكي: التعريف والرتبة والختمة والحفظ والاختبار' },
      univers: { fr: "L'Univers du Savoir : 2500 produits dans 20 domaines", en: 'The Universe of Knowledge: 2,500 products in 20 domains', ar: 'كون المعرفة: ٢٥٠٠ منتج في ٢٠ مجالًا' },
      read: { fr: "READ : le Livre Saint, la lecture verset par verset dans 24 langues, l'écoute et les 114 sourates — sur une seule page", en: 'READ: the Holy Book, verse-by-verse reading in 24 languages, listening and the 114 surahs — on a single page', ar: 'اقرأ: الكتاب المقدس والقراءة آية بآية بـ٢٤ لغة والاستماع والسور الـ١١٤ — في صفحة واحدة' },
      listen: { fr: 'Écouter le Coran avec quatre grands récitateurs', en: 'Listen to the Quran with four great reciters', ar: 'استمع إلى القرآن بأربعة قراء كبار' },
      sourates: { fr: 'Le Coran complet, les 114 sourates et leurs 6236 versets', en: 'The complete Quran, all 114 surahs and their 6236 verses', ar: 'القرآن الكامل، السور الـ114 وآياتها 6236' },
      academy: { fr: "L'Académie NDIAMSYL PEYE : Foriji, Digdaum, Hifz et Kang", en: 'NDIAMSYL PEYE Academy: Foriji, Digdaum, Hifz and Kang', ar: 'أكاديمية نجامسل باي: الفُرْجِي، الدِّدْغَام، الحِفْظ و كَانْغ' },
      learn: { fr: "Apprendre l'alphabet arabe et les bases de la récitation", en: 'Learn the Arabic alphabet and recitation basics', ar: 'تعلم الحروف العربية وأساسيات التلاوة' },
      write: { fr: 'Écrire les lettres arabes et copier les versets', en: 'Write Arabic letters and copy the verses', ar: 'اكتب الحروف العربية وانسخ الآيات' },
      qibla: { fr: 'La Qibla : trouvez la direction de la Sainte Kaaba', en: 'Qibla: find the direction of the Holy Kaaba', ar: 'القبلة: حدد اتجاه الكعبة المشرفة' },
      hijri: { fr: 'Le calendrier Hégirien et les jours spéciaux', en: 'The Hijri calendar and special days', ar: 'التقويم الهجري والأيام المخصوصة' },
      names: { fr: "Les 99 noms d'Allah, Asma-ul-Husna", en: 'The 99 Names of Allah, Asma-ul-Husna', ar: 'أسماء الله الحسنى التسعة والتسعون' },
      videos: { fr: 'Des cours vidéo pour chaque niveau', en: 'Video lessons for every level', ar: 'دروس فيديو لكل مستوى' },
      tools: { fr: 'Les outils : recherche, Tasbih, comparaison et progression', en: 'Tools: search, Tasbih, comparison and progress', ar: 'الأدوات: البحث والتسبيح والمقارنة والتقدم' },
      gallery: { fr: 'La Galerie Royale des produits', en: 'The Royal Products Gallery', ar: 'المعرض الملكي للمنتجات' },
      home: { fr: 'The Quran in 1000 Languages', en: 'The Quran in 1000 Languages', ar: 'القرآن بألف لغة' },
    };
    const intro = intros[key];
    if (intro) {
      import('@/lib/speech').then(({ speakText }) => {
        speakText(intro[uiLang === 'ar' ? 'ar' : uiLang === 'en' ? 'en' : 'fr'], uiLang === 'ar' ? 'ar' : uiLang === 'en' ? 'en' : 'fr', 1);
      });
    }
  };

  const jumpLevel = (lv: AcademyLevel) => {
    setOpenMenu(null);
    setMobileOpen(false);
    setAcademyLevel(lv);
    setSection('academy');
  };

  /* ---- Groupes de navigation ---- */
  const menus: { key: MenuKey; label: string; items: NavItem[] }[] = [
    {
      key: 'academy',
      label: t.nav.academy,
      items: [
        { key: 'academy', label: 'Foriji', desc: t.hub.foriji_desc, icon: <Ear className="h-4 w-4" />, badge: t.hub.foriji_badge },
        { key: 'academy', label: 'Digdaum', desc: t.hub.digdaum_desc, icon: <Volume2 className="h-4 w-4" />, badge: t.hub.digdaum_badge },
        { key: 'academy', label: 'Hifz', desc: t.hub.hifz_desc, icon: <Brain className="h-4 w-4" />, badge: t.hub.hifz_badge },
        { key: 'academy', label: 'Kang', desc: t.hub.kang_desc, icon: <Crown className="h-4 w-4" />, badge: t.hub.kang_badge },
      ],
    },
    {
      key: 'savoir',
      label: t.nav.sciences,
      items: [
        { key: 'sciences', label: t.nav.sciences, desc: t.hub.sciences_desc, icon: <Library className="h-4 w-4" /> },
        { key: 'miracles', label: t.nav.miracles, desc: t.hub.miracles_desc, icon: <Atom className="h-4 w-4" /> },
        { key: 'names', label: t.nav.names, desc: t.hub.names_desc, icon: <Sparkles className="h-4 w-4" /> },
        { key: 'learn', label: t.nav.learn, desc: t.hub.learn_desc, icon: <GraduationCap className="h-4 w-4" /> },
        { key: 'write', label: t.nav.write, desc: t.hub.write_desc, icon: <PenLine className="h-4 w-4" /> },
      ],
    },
    {
      key: 'media',
      label: t.nav.videos,
      items: [
        { key: 'videos', label: t.nav.videos, desc: t.hub.videos_desc, icon: <Video className="h-4 w-4" /> },
        { key: 'conference', label: t.nav.conference, desc: t.hub.conference_desc, icon: <Radio className="h-4 w-4" />, badge: 'Live' },
      ],
    },
    {
      key: 'tools',
      label: t.nav.espace,
      items: [
        { key: 'espace', label: t.nav.espace, desc: t.hub.espace_desc, icon: <Users className="h-4 w-4" />, badge: 'Royal' },
        { key: 'tools', label: t.nav.tools, desc: t.hub.tools_desc, icon: <Wrench className="h-4 w-4" /> },
        { key: 'qibla', label: t.nav.qibla, desc: t.hub.qibla_desc, icon: <Compass className="h-4 w-4" /> },
        { key: 'hijri', label: t.nav.hijri, desc: t.hub.hijri_desc, icon: <CalendarDays className="h-4 w-4" /> },
      ],
    },
  ];

  const levelOf = (item: NavItem): AcademyLevel | null => {
    const l = item.label.toLowerCase();
    return (['foriji', 'digdaum', 'hifz', 'kang'] as string[]).includes(l) ? (l as AcademyLevel) : null;
  };

  /* Page fusionnée READ : THE BOOK + READ + LISTEN + 114 SURATS */
  const mergedActive = section === 'read' || section === 'listen' || section === 'sourates' || section === 'livre';

  const isGroupActive = (m: { key: MenuKey; items: NavItem[] }) =>
    m.key === 'academy' ? section === 'academy' : m.items.some((it) => it.key === section);

  const goHome = () => { setOpenMenu(null); setMobileOpen(false); jump('home'); };

  return (
    <header className="sticky top-0 z-50 border-b border-[rgba(212,175,55,0.25)] bg-[rgba(3,32,25,0.92)] backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center gap-2 px-3 py-2.5 sm:gap-3 sm:px-6">
        {/* Logo */}
        <button
          onClick={goHome}
          className="flex shrink-0 items-center gap-2.5"
          aria-label="THE QURAN IN 1000 LANGUAGES"
        >
          <span className="star-ornament block h-8 w-8 bg-gradient-to-br from-[#f7e7b0] via-[#d4af37] to-[#9a7b1e]" />
          <span className="hidden flex-col items-start leading-none sm:flex">
            <span className="font-serif-royal text-base font-bold tracking-wide text-gold-gradient">THE QURAN</span>
            <span className="text-[10px] font-medium tracking-[0.28em] text-[#f0d98c]/80">IN 1000 LANGUAGES</span>
          </span>
        </button>

        {/* ===== Nav desktop groupée ===== */}
        <nav className="hidden flex-1 items-center justify-center gap-1 md:flex" aria-label="Navigation">
          <button
            onClick={goHome}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-2 text-[13px] font-medium transition-colors',
              section === 'home' ? 'bg-[rgba(212,175,55,0.15)] text-[#f0d98c]' : 'text-[#cfe0cf]/80 hover:bg-[rgba(212,175,55,0.08)] hover:text-[#f0d98c]'
            )}
            aria-current={section === 'home' ? 'page' : undefined}
          >
            <Home className="h-4 w-4" />
          </button>

          {menus.map((m) => (
            <div
              key={m.key}
              className="relative"
              onMouseEnter={() => setOpenMenu(m.key)}
              onMouseLeave={() => setOpenMenu((k) => (k === m.key ? null : k))}
            >
              <button
                onClick={() => setOpenMenu(openMenu === m.key ? null : m.key)}
                className={cn(
                  'flex items-center gap-1 rounded-md px-3 py-2 text-[13px] font-medium transition-colors',
                  openMenu === m.key || isGroupActive(m)
                    ? 'bg-[rgba(212,175,55,0.15)] text-[#f0d98c]'
                    : 'text-[#cfe0cf]/80 hover:bg-[rgba(212,175,55,0.08)] hover:text-[#f0d98c]'
                )}
                aria-expanded={openMenu === m.key}
              >
                {m.label}
                <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', openMenu === m.key && 'rotate-180')} />
              </button>

              {/* Dropdown */}
              {openMenu === m.key && (
                <div
                  className="absolute left-1/2 top-full z-50 mt-2 w-80 -translate-x-1/2 rounded-xl border border-[rgba(212,175,55,0.35)] bg-[rgba(3,32,25,0.98)] p-2 shadow-[0_24px_60px_rgba(0,0,0,0.6)]"
                  role="menu"
                >
                  {m.items.map((it, idx) => (
                    <button
                      key={`${m.key}-${idx}`}
                      onClick={() => {
                        if (m.key === 'academy') jumpLevel(levelOf(it) || 'foriji');
                        else jump(it.key);
                      }}
                      className="flex w-full items-start gap-3 rounded-lg p-2.5 text-left transition-colors hover:bg-[rgba(212,175,55,0.12)]"
                      role="menuitem"
                    >
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[rgba(212,175,55,0.3)] bg-[rgba(212,175,55,0.08)] text-[#d4af37]">
                        {it.icon}
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-2">
                          <span className="text-[13px] font-bold text-[#f0d98c]">{it.label}</span>
                          {it.badge && (
                            <span className="rounded-full border border-[rgba(212,175,55,0.35)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#d4af37]/85">
                              {it.badge}
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 block truncate text-[11px] leading-snug text-[#cfe0cf]/60">{it.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* READ — bouton doré unique (page fusionnée THE BOOK + READ + LISTEN + 114 SURATS) */}
          <button
            onClick={() => jump('read')}
            className={cn(
              'ml-1 flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-bold transition-all',
              mergedActive
                ? 'bg-gradient-to-b from-[#e8c86a] to-[#b8933a] text-[#032019] shadow-[0_4px_20px_rgba(212,175,55,0.4)]'
                : 'gold-glow-hover border border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.1)] text-[#f0d98c] hover:bg-[rgba(212,175,55,0.2)]'
            )}
            aria-current={mergedActive ? 'page' : undefined}
          >
            <BookOpen className="h-4 w-4" />
            <span className="hidden lg:inline">{t.nav.read}</span>
          </button>

          {/* Univers — CTA (2 500 produits) */}
          <button
            onClick={() => jump('univers')}
            className={cn(
              'gold-glow-hover ml-1 flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-bold transition-all',
              section === 'univers' || section === 'gallery'
                ? 'bg-gradient-to-b from-[#e8c86a] to-[#b8933a] text-[#032019] shadow-[0_4px_20px_rgba(212,175,55,0.4)]'
                : 'border border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.1)] text-[#f0d98c] hover:bg-[rgba(212,175,55,0.2)]'
            )}
          >
            <Gem className="h-4 w-4" />
            <span className="hidden lg:inline">{t.nav.univers}</span>
          </button>
        </nav>

        <div className="flex-1 md:hidden" />

        {/* Mode vocal global */}
        <button
          onClick={toggleVoice}
          title={voiceMode ? t.voice.on_hint : t.voice.off_hint}
          className={cn(
            'relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors',
            voiceMode
              ? 'border-[#d4af37] bg-[rgba(212,175,55,0.25)] text-[#f0d98c]'
              : 'border-[rgba(212,175,55,0.3)] text-[#cfe0cf]/75 hover:text-[#f0d98c]'
          )}
          aria-pressed={voiceMode}
        >
          {voiceMode ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          {speaking && voiceMode && (
            <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#d4af37] opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#d4af37]" />
            </span>
          )}
        </button>

        {/* Interface language switcher */}
        <div className="flex shrink-0 items-center gap-1 rounded-lg border border-[rgba(212,175,55,0.3)] bg-[rgba(11,61,46,0.6)] p-1">
          <Languages className="mx-1 hidden h-3.5 w-3.5 text-[#d4af37] sm:block" aria-hidden />
          {UI_LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => setUiLang(l.code)}
              className={cn(
                'rounded px-1.5 py-1 text-xs font-semibold transition-colors sm:px-2',
                uiLang === l.code ? 'bg-[#d4af37] text-[#032019]' : 'text-[#cfe0cf]/75 hover:text-[#f0d98c]'
              )}
              aria-label={l.label}
              title={l.label}
            >
              {l.code.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Burger mobile */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[rgba(212,175,55,0.3)] text-[#f0d98c] md:hidden"
          aria-label="Menu"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </div>

      {/* ===== Panneau mobile ===== */}
      {mobileOpen && (
        <div className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-[rgba(212,175,55,0.2)] bg-[rgba(3,32,25,0.98)] px-4 py-4 md:hidden gold-scroll">
          {/* Accueil + READ + Univers */}
          <div className="mb-4 grid grid-cols-3 gap-2">
            <button
              onClick={goHome}
              className={cn(
                'flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold',
                section === 'home'
                  ? 'border-[#d4af37] bg-[rgba(212,175,55,0.15)] text-[#f0d98c]'
                  : 'border-[rgba(212,175,55,0.3)] text-[#cfe0cf]/85'
              )}
            >
              <Home className="h-4 w-4" /> {t.nav.home}
            </button>
            <button
              onClick={() => jump('read')}
              className={cn(
                'flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-bold',
                mergedActive
                  ? 'bg-gradient-to-b from-[#e8c86a] to-[#b8933a] text-[#032019]'
                  : 'border border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.1)] text-[#f0d98c]'
              )}
            >
              <BookOpen className="h-4 w-4" /> {t.nav.read}
            </button>
            <button
              onClick={() => jump('univers')}
              className={cn(
                'flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-bold',
                section === 'univers'
                  ? 'bg-gradient-to-b from-[#e8c86a] to-[#b8933a] text-[#032019]'
                  : 'border border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.1)] text-[#f0d98c]'
              )}
            >
              <Gem className="h-4 w-4" /> {t.nav.univers}
            </button>
          </div>

          {menus.map((m) => (
            <div key={m.key} className="mb-4">
              <p className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#d4af37]/80">
                {m.key === 'tools' && <Users className="h-3 w-3" />}
                {m.key === 'media' && <Video className="h-3 w-3" />}
                {m.key === 'savoir' && <Library className="h-3 w-3" />}
                {m.key === 'academy' && <Landmark className="h-3 w-3" />}
                {m.label}
              </p>
              <div className="space-y-1.5">
                {m.items.map((it, idx) => (
                  <button
                    key={`${m.key}-${idx}`}
                    onClick={() => {
                      if (m.key === 'academy') jumpLevel(levelOf(it) || 'foriji');
                      else jump(it.key);
                    }}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition-colors',
                      (m.key === 'academy' ? section === 'academy' : section === it.key)
                        ? 'border-[rgba(212,175,55,0.6)] bg-[rgba(212,175,55,0.12)]'
                        : 'border-[rgba(212,175,55,0.18)] bg-[rgba(7,53,40,0.5)]'
                    )}
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[rgba(212,175,55,0.1)] text-[#d4af37]">
                      {it.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-bold text-[#f0d98c]">{it.label}</span>
                      <span className="block truncate text-[11px] text-[#cfe0cf]/60">{it.desc}</span>
                    </span>
                    {m.key === 'academy' && (
                      <span className="shrink-0 rounded-full border border-[rgba(212,175,55,0.35)] px-2 py-0.5 text-[9px] font-semibold uppercase text-[#d4af37]/85">
                        {it.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </header>
  );
}
